import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { interval, Subscription } from 'rxjs';
import { SessionPhotoModalComponent } from 'app/site-owner-sessions/session-photo-modal/session-photo-modal.component';
import { Globals } from 'app/_globals';
import { WorkOrderState } from 'app/_models/work-order-state';
import { FileService } from 'app/_services/file.service';
import { SessionsService } from 'app/_services/sessions.service';
import { SiteWorkOrdersService } from 'app/_services/site-work-orders.service';
import { SiteFormsService } from 'app/_services/site-forms.service';
import { WorkOrderStateService } from 'app/_services/work-order-state.service';
import Swal from 'sweetalert2';
import { AddDocumentsComponent } from './components/add-documents/add-documents.component';
import { ChangeStatusModalComponent } from './components/change-status-modal/change-status-modal.component';
import { EditCompetenciesComponent } from './components/edit-competencies/edit-competencies.component';
import { EditDescriptionModalComponent } from './components/edit-description-modal/edit-description-modal.component';
import { EditMinPartyModalComponent } from './components/edit-min-party-modal/edit-min-party-modal.component';
import { EditSiteFormModalComponent } from './components/edit-site-form-modal/edit-site-form-modal.component';
import { Pagination } from 'app/_models/pagination.model';
import { SiteFormResponse } from 'app/_models/site-forms/site-form-response.model';
import { SiteFormEntryModalComponent } from 'app/shared-pages/site-pages/site-work-orders/site-form-entry/site-form-entry-modal.component';
import { WorkOrderCompetency } from 'app/_models/competency.model';
import { CompetencyService } from 'app/_services/competency.service';
import { NotificationService } from 'app/_services/notification.service';
import { environment } from 'environments/environment';

@Component({
  selector: 'app-work-order',
  templateUrl: './work-order.component.html',
})
export class WorkOrderComponent implements OnInit, OnDestroy {

  public workOrderCompetencies: WorkOrderCompetency[] = [];
  public leadWorkerCompetencies: WorkOrderCompetency[] = [];
  public allWorkerCompetencies: WorkOrderCompetency[] = [];
  public isCompetencyEditingOpenForState = false;


  public routeRole: any;
  public isControlAdmin: boolean;
  private updateSubscription: Subscription;

  public selectedSiteId: any;
  public selectedSite: any;
  public allowCompulsoryCheckOnSiteForm: boolean;
  public allowCompulsoryCheckOffSiteForm: boolean;
  public allowWorkOrderScheduling: boolean;
  public requiresToc: boolean;
  public allowAssetScheduling: boolean;
  public siteFormResponses: SiteFormResponse[] = [];
  public hideBasicEditControls: boolean;

  public workOrderId: any;
  public workOrder: any;

  public workOrderState: WorkOrderState;
  public showSessionsSection: boolean;
  public showContacts: boolean;
  public contacts = [];

  public openSessions: any;
  public allSessions: any;
  public suspendedDetails: any;
  public surrenderedDetails: any;
  public photoCount = 0;

  public exemptFromAssetScheduling: boolean;

  public userSession: any;
  public leadWorkerSession: any;
  public addSessionWorkers: any = [];

  public allowCheckOnFormSubmit: boolean;
  public allowCheckOffFormSubmit: boolean;

  constructor(
    public workOrderStateService: WorkOrderStateService,
    private activatedRoute: ActivatedRoute,
    private sessionsService: SessionsService,
    private workOrderService: SiteWorkOrdersService,
    private competencyService: CompetencyService,
    private siteFormsService: SiteFormsService,
    private modalService: NgbModal,
    private fileService: FileService,
    private globals: Globals,
    private router: Router,
    private notificationService: NotificationService,
  ) {
    this.routeRole = globals.user.routeRole;
    this.isControlAdmin = globals.user.role === 11;

    this.activatedRoute.params.subscribe((params: Params) => {
      this.selectedSiteId = params.id;
      this.workOrderId = params.workOrderId;
    });
  }

  ngOnInit(): void {
    this.showCreateSuccessMessageFromRoute();
    this.setupPage();
    this.updateSubscription = interval(20000).subscribe(() => {
      this.setupPage();
    });
  }

  private showCreateSuccessMessageFromRoute() {
    const createdParam = this.activatedRoute.snapshot.queryParamMap.get('created');
    if (createdParam !== '1') {
      return;
    }

    Swal.fire(
      'Success!',
      'A new work order has been created. You can now add required competencies.',
      'success',
    );

    // Clear one-time "created" query param to prevent showing the success modal again on reload/navigation.
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { created: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  ngOnDestroy(): void {
    if (this.updateSubscription) {
      this.updateSubscription.unsubscribe();
    }
  }

  public setupPage() {
    this.workOrderService.getWorkOrderById(this.workOrderId).subscribe(workOrder => {
      this.workOrder = workOrder;
      this.selectedSite = this.workOrder.asset.site;
      this.hideBasicEditControls = this.isSseContext();
      this.allowCompulsoryCheckOnSiteForm = this.selectedSite.siteOwner.siteOwnerUser.allowCompulsoryCheckOnSiteForm;
      this.allowCompulsoryCheckOffSiteForm = this.selectedSite.siteOwner.siteOwnerUser.allowCompulsoryCheckOffSiteForm;
      this.allowWorkOrderScheduling = this.selectedSite.siteOwner.siteOwnerUser.allowWorkOrderScheduling;
      this.requiresToc = this.workOrder.tocRequired;
      this.allowAssetScheduling = this.selectedSite.siteOwner.siteOwnerUser.allowAssetScheduling;
      this.exemptFromAssetScheduling = this.workOrder.asset.exemptFromAssetScheduling;

      this.getSiteFormResponses();

      this.workOrder.workOrderComments.sort((a, b) => {
        return new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime();
      });

      this.workOrderState = this.workOrderStateService.setWorkOrderState(this.workOrder);
      this.isCompetencyEditingOpenForState = this.workOrderStateService.allowsCompetencyEditingInState(this.workOrderState);
      if (this.workOrderState === WorkOrderState.Surrendered) {
        this.surrenderedDetails = this.getSurrenderedSessionsDetails();
      }

      if (this.workOrderStateService.canHaveSessions(this.workOrderState)) {
        this.sessionsService.getSiteSessionByWorkOrderId(this.workOrderId).subscribe(session => {
          if (!session) { return; }

          this.allowCheckOnFormSubmit = this.workOrder.checkOnSiteFormName;
          this.allowCheckOffFormSubmit = this.workOrder.checkOffSiteFormName;

          this.showSessionsSection = true;
          this.photoCount = session.siteSessionUploads.reduce((runningCount: number, eachUploadBundle) => runningCount + eachUploadBundle.files.length, 0);

          this.sessionsService.getUserSessionsBySiteSessionId(session.id).subscribe(userSessions => {
            this.openSessions = this.getOpenSessionsDetails(userSessions);
            this.allSessions = this.populateSessionsSection(userSessions);
            if (this.workOrderState === WorkOrderState.Suspended) {
              this.suspendedDetails = this.getSuspendedDetails();
            }
          });
        });
      }
    });

    this.competencyService.getWorkOrderCompetencies(this.workOrderId).subscribe((competencies) => {
      this.workOrderCompetencies = competencies;
      this.allWorkerCompetencies = competencies.filter((competency) => competency.applyToParty);
      this.leadWorkerCompetencies = competencies.filter((competency) => !competency.applyToParty);
    });
  }

  private isSseContext(): boolean {
    return Number(this.selectedSite.siteOwner.siteOwnerUser.skylarkUserId) === environment.SSE_OWNER_SKYLARK_ID;
  }

  private getSiteFormResponses() {
    const pagination: Pagination = {
      size: 10000,
      page: 0,
      totalRecords: 0,
    };
    this.siteFormsService.getAllResponsesByWorkOrderId(this.workOrderId, pagination, []).subscribe(responses => {
      this.siteFormResponses = responses.body;
    });
  }

  // progressively add as much details about the suspended status as possible
  // action bar should deal with missing info, or no info at all.
  getSuspendedDetails(): any {
    const lastComment = this.workOrder.workOrderComments[this.workOrder.workOrderComments.length - 1];

    if (!lastComment || lastComment.comment !== 'Work order status updated to: SUSPENDED') {
      return;
    }
    const suspendedTime = new Date(lastComment.createdDate);
    let workerName = lastComment.createdBy;

    let matainenanceName;
    let reasonForSuspended;

    const lastUserSession = this.allSessions[this.allSessions.length - 1];
    if (lastUserSession.workerLogin === workerName) {
      workerName = lastUserSession.workerName;
      matainenanceName = lastUserSession.matainenanceName;
      reasonForSuspended = lastUserSession.notes;
    }

    return {
      suspendedTime: suspendedTime,
      companyName: matainenanceName,
      workerName: workerName,
      reasonForSuspended: reasonForSuspended,
    };
  }

  getSurrenderedSessionsDetails(): any {
    return {
      companyName: (this.workOrder.ownerWorkerUser.maintenance && this.workOrder.ownerWorkerUser.maintenance.name) || '',
      workerName: `${this.workOrder.ownerWorkerUser.firstName} ${this.workOrder.ownerWorkerUser.lastName}`,
      reasonForSurrender: this.workOrder.reasonForSurrender,
      surrenderTime: new Date(this.workOrder.surrenderTime),
    };
  }

  getOpenSessionsDetails(userSessions: any): any {
    const openSessions = userSessions.filter(session => !session.endTime);
    this.leadWorkerSession = openSessions.filter(isAddWorkers => isAddWorkers.owner)[0];
    this.addSessionWorkers = openSessions.filter(isAddWorkers => !isAddWorkers.owner);

    if (openSessions.length > 0) {
      this.userSession = {
        siteSessionId: openSessions[0].siteSession.id,
        workerUserId: openSessions[0].user.id,
      }
    }

    if (openSessions.length === 0) { return; }

    // put the owner first
    openSessions.sort((a, b) => b.owner - a.owner);

    const workers = openSessions.map(session => {
      return {
        name: `${session.user.firstName} ${session.user.lastName}`,
        login: session.user.login,
        companyName: session.user.workerUser && session.user.workerUser.maintenance ? session.user.workerUser.maintenance.name : '',
      };
    });

    return {
      companyName: workers[0].companyName,
      workers: workers,
      guestCount: openSessions[0].guestCount,
    };
  }

  populateSessionsSection(userSessions: any): any {
    return userSessions.map(session => {
      return {
        workerLogin: session.user.login,
        workerName: `${session.user.firstName} ${session.user.lastName}`,
        matainenanceName: session.user.workerUser.maintenance.name,
        startTime: new Date(session.startTime),
        endTime: session.endTime ? new Date(session.endTime) : null,
        reason: session.reason,
        notes: session.notes,
      };
    });
  }

  public onEditDescriptionClick() {
    this.openModalComponent(EditDescriptionModalComponent, 'md');
  }

  public onChangeStatusClick() {
    this.openModalComponent(ChangeStatusModalComponent, 'sm');
  }

  public onEditCompetencies(workerLevel: 'LEAD' | 'ALL') {
    this.openModalComponent(EditCompetenciesComponent, 'lg', undefined, { workerLevel });
  }

  public onAddDocumentsClicked() {
    this.openModalComponent(AddDocumentsComponent, 'md');
  }

  public onEditMinPartyClick() {
    this.openModalComponent(EditMinPartyModalComponent, 'md');
  }

  public onEditSiteFormClick(value) {
    let isCheckOnModal = value === 'Check-On'
    this.openModalComponent(EditSiteFormModalComponent, 'md', isCheckOnModal);
  }

  public changeExemptStatus() {
    let title = 'Make this work order Scheduled?';
    let text = 'A scheduled work order must be started within an asset availability window.';
    if (!this.workOrder.exemptFromAssetScheduling) {
      title = 'Make this work order Unscheduled?';
      text = 'An unscheduled work order can be started at any time.';
    }

    Swal.fire({
      title: title,
      text: text,
      type: 'warning',
      showCancelButton: true,
      customClass: {
        confirmButton: 'confirm-success',
      },
      confirmButtonText: 'Update',
    }).then((result) => {
      if (result.value) {
        const body = {
          exemptFromAssetScheduling: !this.workOrder.exemptFromAssetScheduling,

          // required for the PUT Request:
          id: this.workOrder.id,
          description: this.workOrder.description,
          released: this.workOrder.released,
          approvalStatus: this.workOrder.approvalStatus,
          minimumPartySize: this.workOrder.minimumPartySize,
          checkOnSiteFormId: this.workOrder.checkOnSiteFormId,
          checkOffSiteFormId: this.workOrder.checkOffSiteFormId,
          scheduledStartTime: this.workOrder.scheduledStartTime,
          scheduledEndTime: this.workOrder.scheduledEndTime,
        };

        this.workOrderService.updateWorkOrder(body).subscribe(() => {
          Swal.fire('Updated!', 'Work Order has been updated.', 'success');
          this.setupPage();
        });
      }
    });
  }

  private openModalComponent(
    modalComponent: any,
    modalWidth: 'sm' | 'md' | 'lg',
    isCheckOnModal?,
    modalData?: { workerLevel?: 'LEAD' | 'ALL' },
  ) {
    const modalOptions: NgbModalOptions = { centered: true };
    if (modalWidth === 'sm' || modalWidth === 'lg') {
      modalOptions.size = modalWidth;
    }

    const modal = this.modalService.open(modalComponent, modalOptions);
    modal.componentInstance.workOrder = this.workOrder;
    modal.componentInstance.isCheckOnModal = isCheckOnModal;
    if (modalData && modalData.workerLevel) {
      modal.componentInstance.workerLevel = modalData.workerLevel;
    }

    modal.result.then(result => {
      if (result === 'refresh') {
        this.setupPage();
      }
    }).catch(() => { /* modal dismissed */ });
  }

  public onDeleteWorkOrder() {
    Swal.fire({
      title: 'Delete Work Order?',
      text: 'You will not be able to revert this!',
      type: 'warning',
      showCancelButton: true,
      customClass: {
        confirmButton: 'confirm-danger',
      },
      confirmButtonText: 'Yes, delete it!',
    }).then((result) => {
      if (result.value) {
        this.workOrderService.deleteWorkOrder(this.workOrder.id).subscribe((data: any) => {
          Swal.fire('Deleted!', 'Work Order has been removed.', 'success');

          this.router.navigate(['/' + this.routeRole + '/sites/' + this.selectedSiteId + '/work-orders']);
        });
      }
    });
  }

  public openFile(file) {
    this.fileService.showFile(this.globals.fileFolder.workOrderFiles, file.fileName);
  }

  public deleteFile(file) {
    Swal.fire({
      title: 'Delete RAMS Document?',
      text: 'Are you sure? This action cannot be reverted!',
      type: 'warning',
      showCancelButton: true,
      customClass: {
        confirmButton: 'confirm-danger',
      },
      confirmButtonText: 'Yes, delete it!',
    }).then((result) => {
      if (result.value) {
        this.workOrderService.deleteRams(file.id).subscribe(() => {
          Swal.fire('Deleted!', 'RAMS Document has been deleted.', 'success');
          this.setupPage();
        });
      }
    });
  }

  public viewPhotos() {
    const modalRef = this.modalService.open(SessionPhotoModalComponent, { size: 'lg' });
    modalRef.componentInstance.workOrderId = this.workOrderId;
  }

  public getContacts() {
    this.workOrderService.getAllContactsForWorkOrder(this.workOrderId).subscribe((data: any) => {
      this.contacts = data;
      this.showContacts = true;
    });
  }

  public async addComment() {
    const { value: comment } = await Swal.fire({
      titleText: 'Add Comment to Work Order',
      input: 'textarea',
      inputValidator: (value) => {
        if (value.length === 0) {
          return 'Comments cannot be blank'
        } else if (value.length > 255) {
          return 'Comments cannot be longer than 255 characters'
        }
      },
      inputAttributes: {
        'aria-label': 'Type your comment here'
      },
      showCancelButton: true,
      customClass: {
        confirmButton: 'confirm-success',
      },
      confirmButtonText: 'Save',
    });

    if (comment) {
      const commentBody = {
        workOrderId: this.workOrder.id,
        comment: comment,
      };

      this.workOrderService.addWorkOrderComment(commentBody).subscribe(() => {
        Swal.fire('Updated!', 'Work Order comment added.', 'success');
        this.setupPage();
      });
    }
  }

  public showSiteFormEntryModal(formType: string) {
    const modalOptions: NgbModalOptions = { centered: true, size: 'lg' };
    const modal = this.modalService.open(SiteFormEntryModalComponent, modalOptions);
    modal.componentInstance.workOrder = this.workOrder;
    modal.componentInstance.formType = formType;

    modal.result.then(result => {
      if (result === 'refresh') {
        this.getSiteFormResponses();
      }
    }).catch(() => { /* modal dismissed */ });
  }

  public onCompetencyDelete(competency: WorkOrderCompetency) {
    this.competencyService.deleteWorkOrderCompetency(competency.id).subscribe(
      () => {
        this.notificationService.notify('success', 'Deleted!', 'Competency has been deleted.');
        this.setupPage();
      },
      () => {
        this.notificationService.notify('danger', 'Error', 'There was an error deleting the competency.');
      },
    );
  }

}
